import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';

import { EHeaderTab } from '@/components/ui/SearchHeader/UniversalSearch/UniversalSearchContainer/menu';
import { EClientSearchTriggers, ESearchFilters } from '@/constants/searchFilters';
import { vehicleStyleToTypeMap } from '@/constants/vehicleDetails';
import { useGeoDataMyIp } from '@/hooks/useGeoDataMyIp';
import router from '@/hooks/useRouter';
import { EVehicleType } from '@/redux/modules/globalHeader';
import { applySearchFilter } from '@/redux/modules/search';
import { getHeaderSearchFilters } from '@/redux/selectors/globalHeader';
import {
  getDeliveryCenter,
  getDeliveryDetails,
  getMapboxIdFromPlace,
} from '@/redux/selectors/mapboxPlace';
import { parseFlexibleDateRange } from '@/redux/selectors/queryParams';
import { trackRenterSearchedEvent } from '@/services/analytics/search';
import { ERentalType } from '@/services/analytics/types';
import { parseEventDate } from '@/services/analytics/utils';
import { EDeliveryOption } from '@/services/types/core/delivery.types';
import { getSurfacedLocation } from '@/utility/surfacedLocation';

import { EHomeSearchWidgetVariant } from './HomeSearchWidget';

export const useApplySearch = (variant: EHomeSearchWidgetVariant) => {
  const dispatch = useDispatch();

  const filters = useSelector(getHeaderSearchFilters);
  const deliveryCenter = useSelector(getDeliveryCenter);
  const deliveryDetails = useSelector(getDeliveryDetails);
  const mapboxIdFromPlace = useSelector(getMapboxIdFromPlace);
  const fullMapboxAddress = filters.full_mapbox_address;
  const mapboxId = filters.mapbox_id || mapboxIdFromPlace;

  // Get GeoIP data for delivery mode fallback
  const { data: geoDataMyIp } = useGeoDataMyIp();

  if (variant === EHomeSearchWidgetVariant.Campgrounds) {
    return async () => {
      trackRenterSearchedEvent({
        surfacedLocations: getSurfacedLocation(),
        totalAdults: null,
        totalChildren: null,
        totalInfants: null,
        locationQuery: filters.address || null,
        source: 'campground_search',
        isADAAccessible: false,
        isPetFriendly: false,
        flexibleDateRange: parseFlexibleDateRange(
          filters.dates?.from?.toString(),
          filters.dates?.to?.toString(),
          filters.dates?.flexible_days?.toString(),
        ),
        departureDate: (filters.dates?.from && parseEventDate(filters.dates.from)) || null,
        returnDate: (filters.dates?.to && parseEventDate(filters.dates.to)) || null,
        hasCategories: false,
        selectedCategory: null,
        towableYear: null,
        towableMake: null,
        towableModel: null,
        rentalType: ERentalType.CAMPGROUND,
        searchFilterCategory: null,
        lastQuestionSeen: null,
      });

      await router.push(
        {
          pathname: '/campground-search',
          query: {
            [ESearchFilters.ADDRESS]: filters.address,
            [ESearchFilters.FULL_MAPBOX_ADDRESS]: fullMapboxAddress,
            [ESearchFilters.MAPBOX_ID]: filters.mapbox_id,
            [ESearchFilters.DATE_FROM]:
              filters.dates?.from && filters.dates?.to
                ? dayjs(filters.dates.from).format('YYYY-MM-DD')
                : undefined,
            [ESearchFilters.DATE_TO]:
              filters.dates?.from && filters.dates?.to
                ? dayjs(filters.dates.to).format('YYYY-MM-DD')
                : undefined,
            [ESearchFilters.FLEXIBLE_DAYS]:
              filters.dates?.from && filters.dates?.to
                ? filters.dates.flexible_days?.toString()
                : undefined,
            [ESearchFilters.GUESTS_ADULTS]: filters.guests?.adults
              ? String(filters.guests.adults)
              : undefined,
            [ESearchFilters.GUESTS_CHILDREN]: filters.guests?.children
              ? String(filters.guests.children)
              : undefined,
          },
        },
        undefined,
        { shallow: true },
      );
    };
  }

  return async () => {
    // For delivery mode, use GeoIP location if no address is provided
    let addressToUse = filters.address;
    if (
      variant === EHomeSearchWidgetVariant.RvDelivery &&
      !filters.address &&
      geoDataMyIp?.city &&
      geoDataMyIp?.state
    ) {
      addressToUse = `${geoDataMyIp.city}, ${geoDataMyIp.state}`;
    }

    trackRenterSearchedEvent({
      surfacedLocations: getSurfacedLocation(),
      totalAdults: filters.guests?.adults || 0,
      totalChildren: filters.guests?.children || 0,
      totalInfants: null,
      locationQuery: addressToUse || null,
      source:
        variant === EHomeSearchWidgetVariant.RvDelivery ? 'stationary_delivery' : 'home_search',
      isADAAccessible: !!filters.guests?.accessible,
      isPetFriendly: !!filters.guests?.pet_friendly,
      flexibleDateRange: parseFlexibleDateRange(
        filters.dates?.from?.toString(),
        filters.dates?.to?.toString(),
        filters.dates?.flexible_days?.toString(),
      ),
      departureDate: (filters.dates?.from && parseEventDate(filters.dates.from)) || null,
      returnDate: (filters.dates?.to && parseEventDate(filters.dates.to)) || null,
      hasCategories: true,
      selectedCategory: (filters.vehicleType as unknown as EHeaderTab) || EHeaderTab.ALL,
      ...(filters.vehicleType === EVehicleType.TOWABLE
        ? {
            towableYear: filters.vehicle?.year?.toString() || null,
            towableMake: filters.vehicle?.make?.toString() || null,
            towableModel: filters.vehicle?.model?.toString() || null,
          }
        : {
            towableYear: null,
            towableMake: null,
            towableModel: null,
          }),
      rentalType: ERentalType.RV,
      searchFilterCategory: null,
      lastQuestionSeen: null,
      estimatedMiles: filters.estimated_trip_distance || null,
    });

    await dispatch(
      applySearchFilter(
        {
          [ESearchFilters.ADDRESS]: addressToUse,
          [ESearchFilters.MANUAL_ADDRESS_INPUT]: filters.manual_address_input ? 'true' : 'false',
          [ESearchFilters.FULL_MAPBOX_ADDRESS]: fullMapboxAddress,
          [ESearchFilters.MAPBOX_ID]: filters.mapbox_id,
          [ESearchFilters.DATE_FROM]:
            filters.dates?.from && filters.dates?.to
              ? dayjs(filters.dates.from).format('YYYY-MM-DD')
              : undefined,
          [ESearchFilters.DATE_TO]:
            filters.dates?.from && filters.dates?.to
              ? dayjs(filters.dates.to).format('YYYY-MM-DD')
              : undefined,
          [ESearchFilters.FLEXIBLE_DAYS]:
            filters.dates?.from && filters.dates?.to
              ? filters.dates.flexible_days?.toString()
              : undefined,
          [ESearchFilters.GUESTS_ADULTS]: filters.guests?.adults
            ? String(filters.guests.adults)
            : undefined,
          [ESearchFilters.GUESTS_CHILDREN]: filters.guests?.children
            ? String(filters.guests.children)
            : undefined,
          [ESearchFilters.FILTER_FEATURE]: [
            ...(filters.guests?.pet_friendly ? ['pet_friendly'] : []),
            ...(filters.guests?.accessible ? ['accessible'] : []),
          ],
          [ESearchFilters.FILTER_RENTER_AGE]: filters.driverAge
            ? String(filters.driverAge)
            : undefined,
          ...((!filters.dates?.from && !filters.dates?.to) ||
          (filters.guests?.adults ?? 0) + (filters.guests?.children ?? 0) === 0
            ? { [ESearchFilters.SKIP_DEFAULTS]: 'true' }
            : {}),
          ...(filters.vehicleType === EVehicleType.DRIVABLE
            ? {
                [ESearchFilters.FILTER_TYPE]: vehicleStyleToTypeMap.drivable.join(','),
                [ESearchFilters.ESTIMATED_TRIP_DISTANCE]:
                  filters.estimated_trip_distance?.toString(),
              }
            : {}),
          ...(filters.vehicleType === EVehicleType.TOWABLE
            ? {
                [ESearchFilters.WEIGHT_LESSER]:
                  filters.vehicle?.max_towing_capacity?.toString() || undefined,
                [ESearchFilters.FILTER_TYPE]: vehicleStyleToTypeMap.towable.join(','),
                [ESearchFilters.SUGGESTED_TOWING_CAPACITY]:
                  filters.vehicle?.suggested_towing_capacity?.toString() || undefined,
                [ESearchFilters.TOWING_ID]: filters.vehicle?.id.toString() || undefined,
              }
            : {}),
          ...(variant === EHomeSearchWidgetVariant.RvDelivery ||
          filters.vehicleType === EVehicleType.DELIVERABLES ||
          (deliveryCenter && deliveryDetails)
            ? {
                [ESearchFilters.DELIVERY]: 'true',
                [ESearchFilters.DELIVERY_ADDRESS]: addressToUse,
                [ESearchFilters.DELIVERY_STATIONARY]: EDeliveryOption.MOVING,
                [ESearchFilters.DELIVERY_CENTER]: deliveryCenter,
                [ESearchFilters.DELIVERY_DETAILS]: deliveryDetails,
                ...(mapboxId ? { [ESearchFilters.MAPBOX_ID]: mapboxId } : {}),
                ...(!addressToUse
                  ? { [EClientSearchTriggers.OPEN_STATIONARY_DELIVERY_MODAL]: 'true' }
                  : {}),
              }
            : {}),
        },
        false,
        false,
        true,
        undefined,
        false,
      ),
    );
  };
};
